import type { FC, ReactNode } from 'react'

import { ArrowRight, Phone } from 'lucide-react'
import SectionHeading from '@/components/landing/section-heading'

import { contactPhones, programs } from '@/constants/content'

interface ProgramsSectionProps {
    onSelectProgram: (id: string) => void
}

const ProgramsSection: FC<ProgramsSectionProps> = ({ onSelectProgram }): ReactNode => {
    const primaryPhone = contactPhones[0]

    return (
        <section id='programs' aria-labelledby='programs-title' className='scroll-mt-20 py-20 sm:py-24 lg:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
                <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='programs-title'
                        eyebrow="Programs & fees"
                        title="Pick the track that fits your career."
                        description="Every track is trainer-led and built around hands-on project work. Fees depend on the track and batch, and the team shares the exact amount when you register."
                    />

                    <a
                        href={primaryPhone.href}
                        className='inline-flex shrink-0 items-center gap-2 self-start rounded-lg text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 lg:self-auto'
                    >
                        <Phone className='size-4' aria-hidden='true' />
                        Ask about fees: {primaryPhone.display}
                    </a>
                </div>

                <ul className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {programs.map((program, index) => (
                        <li key={program.id}>
                            <article className='group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 motion-reduce:hover:translate-y-0 sm:p-7'>
                                <div className='flex items-start justify-between'>
                                    <span className='flex size-12 items-center justify-center rounded-xl bg-secondary text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground'>
                                        <program.icon className='size-6' aria-hidden='true' />
                                    </span>

                                    <span className='font-mono text-xs text-muted-foreground' aria-hidden='true'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                </div>

                                <h3 className='mt-6 text-xl font-semibold text-foreground'>
                                    {program.title}
                                </h3>

                                <p className='mt-2 flex-1 text-[15px] leading-relaxed text-muted-foreground'>
                                    {program.summary}
                                </p>

                                <ul className='mt-5 flex flex-wrap gap-2' aria-label={`Skills covered in ${program.title}`}>
                                    {program.skills.map((skill) => (
                                        <li key={skill} className='rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground/75'>
                                            {skill}
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href='#register'
                                    onClick={() => onSelectProgram(program.id)}
                                    className='mt-6 inline-flex items-center gap-1.5 self-start rounded text-sm font-semibold text-primary transition-[gap] hover:gap-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                                >
                                    Register for this track
                                    <span className='sr-only'>: {program.title}</span>
                                    <ArrowRight className='size-4' aria-hidden='true' />
                                </a>
                            </article>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default ProgramsSection
